import React from 'react';
import Head from 'next/head';
import NextSeo from 'next-seo';

import { useTranslations } from '@lang';
import { Facilities } from '@features/Facilities/pages/Facilities';
import * as entitiesActions from '@store/entity/actions';
import * as journeyActions from '@store/yourJourney/actions';

import { yourJourneySelectors } from '@store/yourJourney/selectors';
import { parseDateToString } from '@utils/helpers';
import { NextPage, NextPageContext } from 'next';
import { Property } from 'shared-types/Property';
import { Router } from 'shared-routing';
import { QueryClient, dehydrate } from '@tanstack/react-query';
import {
  getSpaFacilitiesPagePrefetch,
  getSpaSettingsKey,
  getSpaSettings,
  getAvailableSpaFacilitiesPrefetch,
} from '@features/spa/hooks';

type FacilitiesPageProps = {
  propertyTitle: string;
  socialFallbackImage: {
    url: string;
  };
  language: string;
  url: string;
  categoryId: number | null;
};

const FacilitiesPage: NextPage<FacilitiesPageProps> = ({
  categoryId,
  language,
  propertyTitle = '',
  socialFallbackImage = { url: '' },
  url,
}) => {
  const { t } = useTranslations();
  const pageName = t('app.facilities');

  const pageTitle = propertyTitle ? `${pageName} | ${propertyTitle}` : pageName;

  return (
    <>
      <Head>
        <title>{pageTitle}</title>
      </Head>
      <NextSeo
        config={{
          openGraph: {
            title: pageTitle,
            locale: language,
            url,
            images: [{ url: socialFallbackImage && socialFallbackImage.url }],
          },
        }}
      />
      <Facilities categoryId={categoryId} language={language} />
    </>
  );
};

FacilitiesPage.getInitialProps = async ({
  property,
  store,
  language,
  url,
  res,
  query,
}: NextPageContext & {
  property: Property;
  store: any;
  language: string;
  url: string;
  router: any;
}) => {
  if (!property.isSpaIntegrated) {
    if (res) {
      res.writeHead(302, {
        Location: '/',
      });
      res.end();
    } else {
      Router.push('/');
    }
  }
  const client = new QueryClient();
  const { fromDate } = yourJourneySelectors.getParsedJourneyParams(store.getState());
  const spaDate = yourJourneySelectors.getActualSpaDateValue(store.getState());
  const spaDateToSet = spaDate || fromDate;
  const spaSettings = await getSpaSettings(property.id);
  client.setQueryData(getSpaSettingsKey(property.id), { data: spaSettings });

  await Promise.all([
    getSpaFacilitiesPagePrefetch(client, property.id, language),
    store.dispatch(journeyActions.changeJourneyParam('spaDate', spaDateToSet)),
  ]);

  const isCategoryViewVisible = spaSettings?.isFacilityCategoryViewVisible || false;

  await Promise.all([
    store.dispatch(journeyActions.changeJourneyParam('spaDate', spaDateToSet)),
    store.dispatch(entitiesActions.fetchSpaCategories(property.id, language)),
    ...(!isCategoryViewVisible
      ? [
          getAvailableSpaFacilitiesPrefetch(
            client,
            property.id,
            language,
            parseDateToString(query.arrivalDate),
            query.category ? Number(query.category) : null,
            20
          ),
        ]
      : []),
  ]);

  return {
    url,
    language,
    categoryId: query.category ? Number.parseInt(query.category as any, 10) : null,
    propertyTitle: property.propertyTitle,
    socialFallbackImage: property.socialFallbackImage,
    dehydratedState: dehydrate(client),
  };
};

export default FacilitiesPage;
